import React from 'react';
import { Card, Typography, Statistic, Row, Col, Skeleton } from 'antd';
import { ShoppingCartOutlined, FileTextOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const { Text } = Typography;

const RevenueOverview = ({ data, loading }) => {
  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value?.toLocaleString('en-IN') || 0}`;
  };

  const formatYAxis = (value) => {
    if (value >= 10000000) {
      return `${(value / 10000000).toFixed(0)}Cr`;
    } else if (value >= 100000) {
      return `${(value / 100000).toFixed(0)}L`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value;
  };

  if (loading) {
    return (
      <Card size="small" title={<Text strong>Revenue Overview</Text>}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  const chartData = data?.monthly_revenue || [];

  return (
    <Card size="small" title={<Text strong>Revenue Overview</Text>}>
      {/* Summary Stats */}
      <Row gutter={[16, 8]} style={{ marginBottom: '16px' }}>
        <Col xs={8}>
          <Statistic
            title={<span style={{ fontSize: '12px' }}>Total Revenue</span>}
            value={data?.summary?.total_revenue || 0}
            prefix="₹"
            precision={0}
            valueStyle={{ fontSize: '18px', color: '#52c41a' }}
            formatter={(value) => formatCurrency(value).replace('₹', '')}
          />
        </Col>
        <Col xs={8}>
          <Statistic
            title={<span style={{ fontSize: '12px' }}>Items Sold</span>}
            value={data?.summary?.total_items_sold || 0}
            prefix={<ShoppingCartOutlined />}
            valueStyle={{ fontSize: '18px' }}
          />
        </Col>
        <Col xs={8}>
          <Statistic
            title={<span style={{ fontSize: '12px' }}>Invoices</span>}
            value={data?.summary?.total_invoices || 0}
            prefix={<FileTextOutlined />}
            valueStyle={{ fontSize: '18px' }}
          />
        </Col>
      </Row>

      {/* Revenue Trend Chart */}
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10 }}
              angle={-45}
              textAnchor="end"
              height={50}
              interval={0}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 10 }}
              width={50}
            />
            <Tooltip
              formatter={(value) => [`₹${value?.toLocaleString('en-IN')}`, 'Revenue']}
              labelFormatter={(label) => `Month: ${label}`}
              contentStyle={{ fontSize: '12px' }}
            />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#52c41a"
              strokeWidth={2}
              dot={{ fill: '#52c41a', strokeWidth: 2, r: 3 }}
              activeDot={{ r: 5, fill: '#52c41a' }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div style={{
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Text type="secondary">No revenue data available for this period</Text>
        </div>
      )}
    </Card>
  );
};

export default RevenueOverview;
